import React, { Component } from 'react';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';

import { findCompanyBy } from "../helper/ReactComponentHelper";
import FormattedDate from '../fields/dates/FormattedDate';
import FormattedCurrencyValue from '../fields/number/FormattedCurrencyValue';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import ClosedReturnDetailsModal from './ClosedReturnDetailsModal';
import ClosedReturnPreviewModal from './ClosedReturnPreviewModal';

import CLOSED_RETURNS_CONTROLLER from '../../controllers/closedReturns'
import ENUMS, { FilingTypeEnum, VATReturnType, FeatureFlagEnum, Companies } from '../../utils/enums';

import Logger, { LOG_LEVELS } from '../../utils/logger';
import { sanitizeCsvData } from '../../utils/csvSanitizer';
import ExportAll from "../common/ExportAll";
import FILING_CONTROLLER from "../../controllers/filing";
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import ReadOnlyWrapper from '../authorization/ReadOnlyWrapper';

const log = new Logger('CLOSED_RETURNS_SUMMARY_LINE', LOG_LEVELS.DEBUG);
const ENABLE_REOPEN_VATBOOK_FF = FeatureFlagEnum.ENABLE_VIEW_AND_REOPEN_VATBOOK;
const UNKNOWN_COMPANY_TEXT = 'Unknown Company';

class ClosedReturnsSummaryLine extends Component {

  constructor(props) {
    super(props);

    this.state = {
      showVatReturnDetailsModal: false,
      showClosedReturnsPreviewModal: false
    };

    // Reopen action
    this.rowClickReopenHandler = this.rowClickReopenHandler.bind(this);

    // Details Modal
    this.rowClickDetailsHandler = this.rowClickDetailsHandler.bind(this);
    this.onCloseDetailsModal = this.onCloseDetailsModal.bind(this);

    // Preview Modal
    this.rowClickPreviewHandler = this.rowClickPreviewHandler.bind(this);
    this.onClosePreviewModal = this.onClosePreviewModal.bind(this);

    // Export Transactions
    this.handleExportTransactions = this.handleExportTransactions.bind(this);
    this.getLanguageText = this.getLanguageText.bind(this);
    this.renderExportAllButtonSection = this.renderExportAllButtonSection.bind(this);
  }

  getLanguageText(languages, key) {
    return this.props.showToast && languages ? LANGUAGES_CONTROLLER.formatUtilFormatter(languages.data, key) : null;
  }

  rowClickReopenHandler(evt) {
    evt.preventDefault();
    const companyByCode = findCompanyBy('code', this.props.rowDetail.companyCode, this.props.companies);
    const companyId = this.props.rowDetail.companyId || (companyByCode && companyByCode.id);
    const params = Object.assign({}, this.props.rowDetail, { companyId: companyId });
    this.props.clickReopen(params, this.props.filterSelection);
  }

  // Details Modal
  rowClickDetailsHandler(evt) {
    evt.preventDefault();
    this.setState({ showVatReturnDetailsModal: true })
  }
  onCloseDetailsModal() {
    this.setState({ showVatReturnDetailsModal: false })
  }

  // Preview Modal
  rowClickPreviewHandler(evt) {
    evt.preventDefault();
    const row = this.props.rowDetail;

    this.props.getReturnPreview({
      companyId: row.companyCode,
      country: row.country || row.countryCode,
      periodicity: row.returnPeriodicity || row.declarationType,
      reportType: row.returnType,
      startDate: row.periodStartDate,
      endDate: row.periodEndDate,
      fileName: row.fileName,
      filingType: row.filingType
    });
    this.setState({ showClosedReturnsPreviewModal: true });
  }

  onClosePreviewModal() {
    this.setState({ showClosedReturnsPreviewModal: false })
  }

  /**
   * Handler to handle export transactions event
   */
  handleExportTransactions(type) {
    const filterSelection = this.props.filterSelection;
    const filterCompanyId = filterSelection.companyId;
    const isAllCompanies = (filterCompanyId === Companies.ALL_COMPANIES) || (filterCompanyId === -1);

    let companyId = filterCompanyId;
    if (isAllCompanies) {
      const company = findCompanyBy('code', this.props.rowDetail.companyCode, this.props.companies);
      if (company) {
        companyId = company.id;
      }
    }

    this.props.doExportTransactions({
      tenantId: this.props.customerId,
      companyId,
      countryCode: filterSelection.countryCode || this.props.rowDetail.country,
      periodStartDate: this.props.rowDetail.periodStartDate,
      periodEndDate: this.props.rowDetail.periodEndDate,
      frequency: this.props.frequency,
      transactionType: type,
      userName: this.props.userName
    });
  }

  getVatPositionInfo(vatPosition) {
    if (!vatPosition) return null;

    const hasDue = vatPosition.vatDue !== 0;
    const hasRefundable = vatPosition.vatRefundable !== 0;

    if (hasDue && !hasRefundable) {
      return { languageString: 'vatPositionDue', value: vatPosition.vatDue };
    }
    if (hasRefundable) {
      return { languageString: 'vatPositionReclaim', value: vatPosition.vatRefundable };
    }
    return null;
  }

  renderExportAllButtonSection(filingType) {
    return filingType === FilingTypeEnum.VATRETURN ? (
      <>
        <ExportAll
          exportLabel="filingValues_ExportAllSales"
          testId="export-all-sales"
          exportClick={() => this.handleExportTransactions(0)}
          exportFor="ENABLE_EXPORT_VAT_RETURN_TRANSACTIONS"
          showToast={true}
          elementType="inline"
          elementClassName="tertiary divider-left pad-left-xs"
          toastTitle={this.getLanguageText(this.props.languages, "viewTransmission_ExportAllToastTitle")}
          toastDetails={this.getLanguageText(this.props.languages, "viewTransmission_ExportAllToastDetails")}
          excludeFromReadonly={true}
        />
        &nbsp;
        <ExportAll
          exportLabel="filingValues_ExportAllPurchases"
          testId="export-all-purchases"
          exportClick={() => this.handleExportTransactions(1)}
          exportFor="ENABLE_EXPORT_VAT_RETURN_TRANSACTIONS"
          showToast={true}
          elementType="inline"
          elementClassName="tertiary divider-left pad-left-xs"
          toastTitle={this.getLanguageText(this.props.languages, "viewTransmission_ExportAllToastTitle")}
          toastDetails={this.getLanguageText(this.props.languages, "viewTransmission_ExportAllToastDetails")}
          excludeFromReadonly={true}
        />
      </>
    ) : null;
  }

  render() {
    log.debug('render(): ', this.props);

    const row = this.props.rowDetail || {};
    const company = findCompanyBy('code', row.companyCode, this.props.companies);
    const companyName = company ? company.name : UNKNOWN_COMPANY_TEXT;
    const periodName = ENUMS.getPeriodicityFromId(row.returnPeriodicity);

    // Hide reopen for returns closed via direct submission (isAutoSubmission=true).
    const canShowReopen = row.canReopen && !row.isAutoSubmission;
    let reOpenBtn;
    if (canShowReopen) {
      reOpenBtn = (
        <span>
          <a className="tertiary divider-left pad-left-xs pad-right-xs" onClick={this.rowClickReopenHandler}>
            <LanguageStringResolver string='returnsHistory_Reopen' />
          </a>
        </span>
      );
    }

    // VAT Return specific elements
    const isVATReturn = (row.filingType === 'VATReturn');
    let viewDetailsBtn;
    if (isVATReturn) {
      viewDetailsBtn = (
        <span>
          <a className="tertiary divider-right pad-right-xs" onClick={this.rowClickDetailsHandler} excludeFromReadonly={true}>
            <LanguageStringResolver string='viewDetails' />
          </a>
          <span className="pad-right-xs"></span>
          <ClosedReturnDetailsModal
            showModal={this.state.showVatReturnDetailsModal}
            onCloseModal={this.onCloseDetailsModal}
            vatReturnDetails={row}
            companyName={companyName}
            periodName={periodName}
          />
        </span>
      );
    }

    // VAT Position block
    let vatPositionBlock;
    if (isVATReturn) {
      const positionInfo = this.getVatPositionInfo(row.vatPosition);
      if (positionInfo) {
        vatPositionBlock = (
          <span>
            <div>
              <span className="float-left semi-bold"><LanguageStringResolver string={positionInfo.languageString} /></span>
              <FormattedCurrencyValue value={positionInfo.value} />
            </div>
          </span>
        );
      }
    }

    // Correction suffix
    const isCorrection = isVATReturn && row.vatType === VATReturnType.getValue(VATReturnType.CORRECTION);
    const correctionSuffixLabel = isCorrection ? <LanguageStringResolver string="closedReturnsPage_CorrectionSuffix" /> : null;

    // Download button
    const filingName = ENUMS.getFilingNameFromFilingType(row.filingType);
    const countryCode = row.country || row.countryCode;
    const periodicity = ENUMS.getPeriodicityFromId(row.returnPeriodicity);
    const downloadFilename = `${filingName}_${countryCode}_${periodicity}.csv`;
    const downloadBtn = (
      <CSVLink data={sanitizeCsvData(buildCSVData(row, this.props.featureFlags?.[ENABLE_REOPEN_VATBOOK_FF]))} filename={downloadFilename} className='tertiary divider-left pad-left-xs pad-right-xs' excludeFromReadonly={true}>
        <LanguageStringResolver string='returnsHistory_Export' />
      </CSVLink>
    );

    // Preview button
    const previewBtn = (
      <span>
        <a className="tertiary pad-right-xs" data-testid="close-return-preview" onClick={this.rowClickPreviewHandler} excludeFromReadonly={true}>
          <LanguageStringResolver string='returnsHistory_Preview' />
        </a>
        <ClosedReturnPreviewModal
          showModal={this.state.showClosedReturnsPreviewModal}
          onCloseModal={this.onClosePreviewModal}
          data={this.props.previewData}
          isLoading={this.props.previewLoading}
        />
      </span>
    );

    // Return type display
    let returnTypeDisplay = filingName;
    if (row.filingType === 'Intrastat') {
      returnTypeDisplay = ENUMS.getIntrastatReturnTypeDisplay(row.returnType);
    }

    // Prior to SVR-5675 there was a VAT Number shown against the company, it was removed as part of the refactor
    // this is because the closed return isn't saved with a vat number, so it won't always be correct
    // if the company changes vat number later, it will show the new number on old returns
    // if the company has >1 vat number for the same country, the code was picking the first in api response
    // correct fix for this would be at desktop to save the vat number as part of the history table, then include in
    // api response so it could be added back here.

    return (
      <ReadOnlyWrapper>
        <tr>
          <td>
            <span className="semi-bold">{returnTypeDisplay}{correctionSuffixLabel}<br /></span>
            {vatPositionBlock}
          </td>
          <td>
            <span className="semi-bold"><FormattedDate value={row.periodStartDate} /></span>
          </td>
          <td>
            <span className="semi-bold"><FormattedDate value={row.periodEndDate} /></span>
          </td>
          <td>
            <span>{companyName}</span>
          </td>
          <td>
            <span>{viewDetailsBtn}{previewBtn}{downloadBtn}{reOpenBtn}</span>
            {this.renderExportAllButtonSection(row.filingType)}
          </td>
        </tr>
      </ReadOnlyWrapper>
    );
  }
}


ClosedReturnsSummaryLine.defaultProps = {
  rowDetail: {},
  clickReopen: () => { },
  filterSelection: {}
};

export function mapStateToProps(state) {
  log.debug('mapStateToProps(): ', state);
  return {
    showToast: state.filing?.showToast,
    languages: state.languages,
    companies: state.companies?.data || [],
    previewData: state.closedReturns?.previewData || {},
    previewLoading: state.closedReturns?.loading || false,
    customerId: state.authentication?.data?.customerId,
    userName: state.authentication?.data?.userName || "Unknown",
    featureFlags: state.featureFlag?.data
  };
}

export function mapDispatchersToProps(dispatch) {
  return {
    getReturnPreview: (params) => {
      log.debug('getReturnPreview: params: ', params);
      dispatch(CLOSED_RETURNS_CONTROLLER.getReturnPreview(params))
    },
    doExportTransactions: (params) => {
      dispatch(FILING_CONTROLLER.doExportTransactions(params));
    }
  }
}

// Optimized by Amazon Q: GenAI
export function buildCSVData(rowDetail, isReopenVatBookFFEnabled) {
  log.debug('getCSVData(): rowDetail: ', rowDetail);
  const baseData = {
    "Company Code": rowDetail.companyCode,
    "Declaration Type": ENUMS.getPeriodicityFromId(rowDetail.returnPeriodType || rowDetail.declarationType),
    "Period Start Date": rowDetail.periodStartDate,
    "Period End Date": rowDetail.periodEndDate
  };

  const filingType = rowDetail.filingType;

  if (filingType === FilingTypeEnum.VATRETURN) {
    return rowDetail.boxes
      .sort((a, b) => a.boxType - b.boxType)
      .map(entry => ({
        ...baseData,
        "Box Type": ENUMS.getVATBoxTypeFromId(entry.boxType),
        "Box Name": entry.boxName,
        "Box Value": (+entry.boxValue).toFixed(2)
      }));
  }

  if (filingType === FilingTypeEnum.INTRASTAT) {
    return [{ ...baseData, "Return Type": ENUMS.getIntrastatReturnTypeDisplay(rowDetail.returnType) }];
  }

  const lineBasedClosedReturnTypes = [FilingTypeEnum.ECLISTING, FilingTypeEnum.LOCALLISTING, FilingTypeEnum.OSSIMPORT, FilingTypeEnum.OSSUNION, FilingTypeEnum.OSSNONUNION];
  if (lineBasedClosedReturnTypes.includes(filingType)) {
    return [{ ...baseData, "Return Type": filingType }];
  }

  if (filingType === FilingTypeEnum.VATBOOKS) {
    return isReopenVatBookFFEnabled ? [{ ...baseData, "Return Type": filingType }] : [];
  }

  log.debug("Filing return type %s not matched to a csv output mapping", filingType);
  return [];
}
export default connect(mapStateToProps, mapDispatchersToProps)(ClosedReturnsSummaryLine);
